import { useAppTranslation } from '@flowus/common/i18n';
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { cx } from '@flowus/common/cx';
import { Select } from './select';
import type { FC } from 'react';
import { useChartBlockId } from './chart-config-context';
import { getProperty } from 'src/utils/collection-util';
import { useStore } from 'react-redux';
import { Icon } from 'src/common/components/icon';
import { useSelectProperty } from './use-select-property';
import { useUpdateChartFormat, useChartFormat } from 'src/hooks/chart';
import { Switch } from '@flowus/common/components';
import { MultiSwitchText } from './multi-switch-text';
import { CollectionSchemaType, GroupSortType, type ChartFormat } from '@next-space/fe-api-idl';
import { invalidProperty } from './data';

interface Props {}
export const XAxisField: FC<Props> = () => {
  const { t } = useAppTranslation();
  const ORDER_ACCORDING: { orderBy: ChartFormat['orderBy']; desc: string; mapping: string }[] = [
    {
      orderBy: 'xAxis',
      desc: t('editor.component.chart.chart_config_panel.x_axis_field.x_axis_value'),
      mapping: t('editor.component.chart.chart_config_panel.x_axis_field.sector_field_value'),
    },
    {
      orderBy: 'yAxis',
      desc: t('editor.component.chart.chart_config_panel.x_axis_field.y_axis_value'),
      mapping: t('editor.component.chart.chart_config_panel.x_axis_field.sector_value'),
    },
  ];
  const orderRules: { orderRule: ChartFormat['orderRule']; desc: string }[] = [
    {
      orderRule: GroupSortType.ASCENDING,
      desc: t('common.ascending'),
    },
    {
      orderRule: GroupSortType.DESCENDING,
      desc: t('common.descending'),
    },
  ];

  const store = useStore();
  const chartBlockId = useChartBlockId();
  const format = useChartFormat(chartBlockId);
  const collectionId = format?.ref?.uuid;
  const xAxisPropertyId = format?.xAxisPropertyId ?? 'title';
  const xAxisPropertyGroupLevel = format?.xAxisPropertyGroupLevel;
  const includeEmpty = format?.includeEmpty ?? true;
  const splitOption = format?.splitOption ?? true;
  const orderBy = format?.orderBy ?? 'xAxis';
  const orderAccording = ORDER_ACCORDING.map((v) => {
    return {
      ...v,
      desc:
        format?.chartMode?.type === 'Pie'
          ? t('editor.component.chart.chart_config_panel.x_axis_field.sector_field_value')
          : t('editor.component.chart.chart_config_panel.x_axis_field.x_axis_value'),
    };
  });
  const orderByValue = orderBy === 'xAxis' ? orderAccording[0]! : orderAccording[1]!;

  const orderRule = format?.orderRule ?? GroupSortType.ASCENDING;
  const orderRuleValue =
    orderRule === GroupSortType.ASCENDING
      ? {
          orderRule: GroupSortType.ASCENDING,
          desc: t('common.ascending'),
        }
      : {
          orderRule: GroupSortType.DESCENDING,
          desc: t('common.descending'),
        };

  const property =
    getProperty(store, collectionId, xAxisPropertyId, xAxisPropertyGroupLevel) ??
    (format?.xAxisPropertyId ? invalidProperty : undefined);
  const showSplitOption =
    property?.type === CollectionSchemaType.MULTI_SELECT ||
    property?.type === CollectionSchemaType.FILE ||
    property?.type === CollectionSchemaType.RELATION ||
    property?.type === CollectionSchemaType.PERSON;
  const selectProperty = useSelectProperty();
  const updateChartFormat = useUpdateChartFormat();
  const isPieChart = format?.chartMode?.type === 'Pie';
  if (format?.chartMode?.type === 'Number') return null;

  const xAxisPropertyNode = property ? (
    <div className="flex items-center">
      {property.icon && <Icon name={property.icon} size="middle" />}
      <div
        className={cx('text-ellipsis max-w-[124px] ml-0.5', {
          'text-red': invalidProperty === property,
        })}
      >
        {property?.title}
      </div>
    </div>
  ) : undefined;

  return (
    <>
      <div className="mt-5 text-t2-medium">
        {t('editor.component.chart.chart_config_panel.x_axis_field.x_axis')}
      </div>
      <div className="text-t2 mt-2 space-y-3.5">
        <div className="flex items-center justify-between">
          <div>{t('editor.component.chart.chart_config_panel.x_axis_field.field')}</div>
          <Select
            className={cx({ 'cursor-not-allowed': !collectionId })}
            title={xAxisPropertyNode}
            showBorder={false}
            placeHolder={t('common.select_field')}
            onClick={(e) => {
              collectionId &&
                selectProperty(collectionId, {
                  popcorn: e.currentTarget,
                  callback: (propertyId, groupLevel) => {
                    updateChartFormat(chartBlockId, {
                      xAxisPropertyId: propertyId,
                      xAxisPropertyGroupLevel: groupLevel,
                    });
                  },
                });
            }}
          />
        </div>
        {showSplitOption && (
          <div className="flex items-center justify-between">
            <div>
              {t(
                'editor.component.chart.chart_config_panel.x_axis_field.multi_option_split_statistics'
              )}
            </div>
            <Switch
              open={splitOption}
              onSwitch={(open) => {
                updateChartFormat(chartBlockId, {
                  splitOption: open,
                });
              }}
            />
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            {t('editor.component.chart.chart_config_panel.x_axis_field.include_empty_data')}
          </div>
          <Switch
            open={includeEmpty}
            onSwitch={(open) => {
              updateChartFormat(chartBlockId, {
                includeEmpty: open,
              });
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>{t('editor.component.chart.chart_config_panel.x_axis_field.sort_by')}</div>
          <MultiSwitchText
            active={orderByValue!}
            values={orderAccording}
            onSwicth={(value) => {
              updateChartFormat(chartBlockId, {
                orderBy: value.orderBy,
              });
            }}
          />
        </div>
        {!isPieChart && (
          <div className="flex items-center justify-between">
            <div>{t('editor.component.chart.chart_config_panel.x_axis_field.sort_rule')}</div>
            <MultiSwitchText
              active={orderRuleValue!}
              values={orderRules}
              onSwicth={(v) => {
                updateChartFormat(chartBlockId, {
                  orderRule: v.orderRule,
                });
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};
